import React, { useEffect, useState } from 'react';
import { Box, IconButton, Link, Popover, Tooltip, Typography } from '@material-ui/core';
import EventNoteIcon from '@material-ui/icons/EventNote';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { GetHelp, InfoButton } from '../../FormWidgets/Description';
import { ShowSetting } from '../../FormWidgets/ShowSliderSetting';
import {
  scheduledFieldBinding, scheduledIrrigationEventRows,
  projectScheduledIrrigationEvents, splitScheduledIrrigationEvents
} from '../../Utils/scheduling_utils';
import { useSavedSettings } from '../settingFunctions';
import { LoadSettingsDialog } from '../../Utils/load_settings';
import useScheduleDraft from './useScheduleDraft';
import _ from 'lodash';

export function useScheduledSettings(controller, parent, category, ordinaryValues, ordinaryControls) {
  const draft = useScheduleDraft({ controller, parent });
  // Ordinary tabs follow the controller's enabled state, never a retained
  // draft's enable switch. Inactive recipes are edited/saved in Scheduling.
  const enabled = Boolean(draft.baseline?.schedule_meta?.schedule_enabled);
  const recipe = {
    ...draft,
    context: enabled ? draft.context : null,
    dirty: enabled && draft.dirty,
    writable: enabled && draft.writable,
    save: () => Boolean(parent.settings.scheduling?.schedule_meta?.schedule_enabled) && draft.save(),
    reset: () => { if (enabled) draft.reset(); }
  };
  const [ordinaryResult, setOrdinaryResult] = useState(null);
  const binding = (path, index) => scheduledFieldBinding(recipe.context, category, path, index);
  const eventField = (path) => category === 'irrigation' && /^irrigation_events\[\d+\](?:\.|$)/.test(path);
  const projectedEvents = category === 'irrigation'
    ? projectScheduledIrrigationEvents(recipe.context, ordinaryValues?.irrigation_events) : [];
  const schedule = (path, index) => eventField(path)
    ? binding('irrigation_events', _.get({ irrigation_events: projectedEvents }, path.replace(/\]\..*$/, ']') + '.irrigation_index'))
    : binding(path, index);
  const liveController = useSnapshot(controller);
  const permissionCategory = category === 'irrigation' ? 'nutrition' : category;
  const categoryWritable = Boolean(liveController.access_rights?.[permissionCategory]?.write);
  const canWriteOrdinary = () => Boolean(controller.access_rights?.[permissionCategory]?.write);
  const paths = (value, prefix = '') => Object.entries(value ?? {}).flatMap(([key, entry]) => {
    const path = Array.isArray(value) ? `${prefix}[${key}]` : prefix ? `${prefix}.${key}` : key;
    return entry && typeof entry === 'object' && !Array.isArray(entry) ? paths(entry, path) :
      Array.isArray(entry) && !/pumps$|^irrigation_events$/.test(path) ? paths(entry, path) : [path];
  });
  const values = _.cloneDeep(ordinaryValues);
  paths(values).forEach((path) => {
    const selected = binding(path);
    if (selected) {
      const value = selected.get(recipe.context.phase);
      _.set(values, path, /pumps$/.test(path) && Array.isArray(value)
        ? value.map((pump, index) => ({
          ...pump, name: _.get(ordinaryValues, `${path}[${index}].name`, '')
        })) : value);
    }
  });
  if (category === 'irrigation' && values)
    values.irrigation_events = projectScheduledIrrigationEvents(recipe.context, ordinaryValues.irrigation_events);
  const savedPhase = recipe.baseline?.schedule_values?.scheduling_values.find(
    (phase) => phase.phase_id === recipe.context?.phaseId
  );
  const route = (names, nextValues, saveTemp = true) => {
    let entries = Array.isArray(names) ? names.map((name, i) => [name, nextValues[i]]) : [[names, nextValues]];
    if (entries.some(([path]) => eventField(path))) {
      // Event controls edit fields by their displayed index. Rebuild that view
      // before partitioning it; its indexes differ from either backing array.
      const composite = { irrigation_events: _.cloneDeep(projectedEvents) };
      entries.filter(([path]) => path === 'irrigation_events' || eventField(path))
        .forEach(([path, value]) => _.set(composite, path, value));
      entries = entries.filter(([path]) => path !== 'irrigation_events' && !eventField(path));
      entries.push(['irrigation_events', composite.irrigation_events]);
    }
    const phase = _.cloneDeep(recipe.context?.phase);
    const phaseEntries = new Map();
    const ordinaryEntries = [];
    for (const [path, value] of entries) {
      if (category === 'irrigation' && path === 'irrigation_events' &&
          scheduledIrrigationEventRows(recipe.context).length) {
        const split = splitScheduledIrrigationEvents(recipe.context, value, ordinaryValues.irrigation_events);
        if (split.phase && recipe.writable && !recipe.stale)
          phaseEntries.set(`schedule_values.scheduling_values[${recipe.phaseIndex}].irrigation_events`, split.phase);
        if (split.ordinary && canWriteOrdinary()) ordinaryEntries.push([path, split.ordinary]);
        continue;
      }
      const selected = binding(path);
      if (selected) {
        if (!recipe.writable || recipe.stale || recipe.context.phase._deleted) continue;
        const converted = selected.set(phase, /pumps$/.test(path)
          ? value.map((pump) => _.omit(pump, 'name')) : value);
        _.set(phase, selected.path, converted);
        phaseEntries.set(`schedule_values.scheduling_values[${recipe.phaseIndex}].${selected.path}`, converted);
      } else if (canWriteOrdinary()) ordinaryEntries.push([path, value]);
    }
    if (ordinaryEntries.length)
      ordinaryControls.setValue(ordinaryEntries.map(([path]) => path), ordinaryEntries.map(([, value]) => value), saveTemp);
    if (phaseEntries.size)
      recipe.controls.setValue([...phaseEntries.keys()], [...phaseEntries.values()], saveTemp);
    setOrdinaryResult(null);
  };
  const savedEventValue = (path) => {
    const [, index, field] = /^irrigation_events\[(\d+)\](?:\.(.*))?$/.exec(path);
    const event = projectedEvents[Number(index)];
    const row = Number(event.irrigation_index);
    const position = projectedEvents.slice(0, Number(index)).filter(
      (entry) => Number(entry.irrigation_index) === row).length;
    const savedEvents = schedule(path)
      ? savedPhase?.irrigation_events ?? []
      : parent.settings.irrigation?.irrigation_events ?? [];
    const saved = savedEvents.filter((entry) => Number(entry.irrigation_index) === row)[position];
    return field ? _.get(saved, field) : saved;
  };
  const canReset = (path, index) => {
    if (eventField(path)) return savedEventValue(path) !== undefined;
    const selected = binding(path, index);
    return !selected || Boolean(savedPhase && selected.get(savedPhase) !== undefined);
  };
  const save = (onlyChanged, ...args) => {
    if (enabled && parent.schedule_submission?.status === 'pending') return false;
    const ordinaryDirty = paths(ordinaryValues).some((path) => ordinaryControls.hasChanged(path));
    const ordinaryWritable = canWriteOrdinary();
    let sent = true;
    if ((ordinaryDirty || (!onlyChanged && !recipe.dirty)) && ordinaryWritable) {
      sent = ordinaryControls.saveSettings(...args);
      setOrdinaryResult(sent ? 'sent' : 'unsent');
    }
    if (recipe.dirty || (!onlyChanged && !ordinaryWritable && recipe.writable)) sent = recipe.save() && sent;
    return sent;
  };
  const controls = ordinaryControls ? {
    ...ordinaryControls,
    retainSetting(path) {
      if (!binding(path) && !eventField(path)) ordinaryControls.retainSetting?.(path);
    },
    setValue: route,
    saveTempSettings: route,
    resetSetting(path) {
      if (!canReset(path)) return;
      if (eventField(path)) { route(path, savedEventValue(path)); return; }
      const selected = binding(path);
      if (selected) route(path, selected.get(savedPhase));
      else ordinaryControls.resetSetting(path);
    },
    hasChanged(path) {
      if (eventField(path)) return !_.isEqual(_.get(values, path), savedEventValue(path));
      const selected = binding(path);
      return selected ? !_.isEqual(selected.get(recipe.context.phase), selected.get(savedPhase)) :
        ordinaryControls.hasChanged(path);
    },
    saveSettings: (...args) => save(false, ...args),
    saveChangedSettings: (...args) => save(true, ...args),
    resetSettings() {
      ordinaryControls.resetSettings();
      recipe.reset();
      setOrdinaryResult(null);
    },
    setValueMinMaxSlider(minPath, maxPath, min, max, saveTemp = true) {
      route([minPath, maxPath], [min, max], saveTemp);
    }
  } : recipe.controls;
  Object.assign(schedule, {
    recipe, values, controls, ordinaryResult, canReset,
    writable: recipe.writable && !recipe.stale && !recipe.context?.phase._deleted,
    canSave: categoryWritable || recipe.writable,
    saving: enabled && draft.submission?.status === 'pending',
    disabled: (path, index, ordinaryDisabled = !categoryWritable) =>
      schedule(path, index) ? !recipe.writable || recipe.stale || Boolean(recipe.context?.phase._deleted) : ordinaryDisabled,
    savedValue: (path, index, fallback) => category === 'irrigation' && path === 'irrigation_events'
      ? projectedEvents.map((_, i) => savedEventValue(`irrigation_events[${i}]`))
      : eventField(path) ? savedEventValue(path) : binding(path, index)?.get(savedPhase) ?? fallback,
    setValues(next) {
      const changed = paths(next).filter((path) => !_.isEqual(_.get(next, path), _.get(values, path)));
      if (changed.length) route(changed, changed.map((path) => _.get(next, path)));
    }
  });
  return schedule;
}

function RestoreReady({ recipe }) {
  const { parent, controls } = recipe;
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    parent, controls, 'scheduling',
    () => !parent.schedule_draft_loaded && !_.isEmpty(parent.temp_settings.scheduling)
  );
  useEffect(() => {
    if (_.isEmpty(parent.temp_settings.scheduling)) parent.schedule_draft_loaded = true;
  }, [parent]);
  return <LoadSettingsDialog open={openLoadSettingsDialog} handleClose={closeLoadSettingsDialog} controls={controls} info="scheduling" />;
}

export function ScheduleDraftRestore({ recipe }) {
  return recipe.ready ? <RestoreReady recipe={recipe} /> : null;
}

export function ScheduleDraftStatus({ schedule }) {
  const { t } = useTranslation();
  const help = GetHelp('schedule_editing_phase');
  const recipe = schedule.recipe ?? schedule;
  return (
    <>
      <ScheduleDraftRestore recipe={recipe} />
      {(recipe.context || recipe.dirty) && <Box my={2}>
        {recipe.context && <Box display="flex" alignItems="center" gridGap={8}>
          <ScheduleMarker target={recipe.context} showSaveScope />
          <Typography style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {t('schedule_editing_phase', { phase: recipe.context.phaseNumber })}
            {recipe.context.phaseName ? ` · ${recipe.context.phaseName}` : ''}
          </Typography>
          <InfoButton content={help.content} />
        </Box>}
      </Box>}
      {schedule.ordinaryResult === 'unsent' && <Alert severity="warning">
        {t(`schedule_ordinary_${schedule.ordinaryResult}`)}
      </Alert>}
    </>
  );
}
export function ScheduleMarker({ target, showSaveScope = false }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  if (!target) return null;
  const phase = `${t('phase')} ${target.phaseNumber}${target.phaseName ? ` · ${target.phaseName}` : ''}`;
  const label = `${t('scheduling')}: ${phase}${target.dirty ? ` · ${t('schedule_unsaved_changes')}` : ''}`;
  return (
    <>
      <Tooltip title={label}>
        <IconButton size="small" style={{ minWidth: 44, minHeight: 44 }} color={target.dirty ? 'secondary' : 'primary'}
          aria-label={label} aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
          onClick={(event) => setAnchor(event.currentTarget)}>
          <EventNoteIcon />
        </IconButton>
      </Tooltip>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box p={2} maxWidth={320} role="dialog" aria-label={label}>
          <Typography>{phase}</Typography>
          {target.dirty && <Typography color="secondary">{t('schedule_unsaved_changes')}</Typography>}
          {showSaveScope && <Typography variant="body2">{t('schedule_recipe_scope')}</Typography>}
          {target.managed && <Typography variant="body2">{t('schedule_managed_help')}</Typography>}
          <Link href={`#${target.href}`} onClick={() => setAnchor(null)}>
            {t(target.managed ? 'schedule_edit_schedule' : 'schedule_edit_phase')}
          </Link>
        </Box>
      </Popover>
    </>
  );
}

/** The displayed value and callbacks belong to the selected ordinary or recipe scope. */
export function ScheduledSetting({ schedule, scheduleIndex, toDisplay = (value) => value, children, ...props }) {
  const target = schedule?.(props.id, scheduleIndex);
  return (
    <ShowSetting {...props}
      valueActions={<>{props.valueActions}<ScheduleMarker target={target} /></>}
      cur_value={target ? toDisplay(target.get(schedule.recipe.context.phase)) : props.cur_value}
      controller_value={target
        ? toDisplay(schedule.savedValue(props.id, scheduleIndex, props.controller_value))
        : props.controller_value}
      disabled={props.disabled || (target && !schedule.writable)}
      resetDisabled={props.resetDisabled || (target && !schedule.canReset(props.id, scheduleIndex))}>
      {children}
    </ShowSetting>
  );
}

export function ScheduleManagedNotice({ target }) {
  const { t } = useTranslation();
  if (!target) return null;
  return (
    <Box my={1}>
      <ScheduleMarker target={target} />
      <InfoButton content={t('schedule_managed_help')} />
    </Box>
  );
}
